import React__default, { useState, useEffect } from 'react';
import { PriceContainer, DigitContainer, DigitStack, DigitItem } from './styles.js';
import { findDiffIndex, generateDigitSequence, shouldAnimateDigit, calculateDigitDirection } from './utils.js';
import { jsx } from '@emotion/react/jsx-runtime';

const defaultFormatter = Intl.NumberFormat('en-US', {
  currency: 'USD',
  style: 'currency'
}).format;
const AnimatedDigit = ({
  currentDigit,
  targetDigit,
  direction,
  isPositive
}) => {
  const sequence = generateDigitSequence(currentDigit, targetDigit, direction);
  return jsx(DigitContainer, {
    children: jsx(DigitStack, {
      direction: direction,
      stepCount: sequence.length,
      isPositive: isPositive,
      children: sequence.map((digit, index) => jsx(DigitItem, {
        isAnimating: direction !== 'none',
        children: digit
      }, `${digit}-${index}`))
    })
  });
};
const PriceRolodex = React__default.memo(({
  price,
  formatter = defaultFormatter
}) => {
  const [currentPrice, setCurrentPrice] = useState(price);
  const [digits, setDigits] = useState(() => formatter(price).split('').map((char, index) => ({
    currentDigit: char,
    targetDigit: char,
    direction: 'none',
    key: `${index}-${char}-initial`,
    isPositive: false
  })));
  useEffect(() => {
    if (price === currentPrice) return;
    const currentPriceFormatted = formatter(currentPrice);
    const newPriceFormatted = formatter(price);
    const isIncreasing = price > currentPrice;
    const diffIndex = findDiffIndex(currentPriceFormatted, newPriceFormatted);
    const newDigits = [];
    for (let i = 0; i < newPriceFormatted.length; i++) {
      const currentChar = currentPriceFormatted[i] || '';
      const newChar = newPriceFormatted[i] || '';
      const direction = shouldAnimateDigit(diffIndex, i, currentChar, newChar) ? calculateDigitDirection(currentChar, newChar) : 'none';
      newDigits.push({
        currentDigit: currentChar,
        targetDigit: newChar,
        direction,
        isPositive: isIncreasing,
        key: `${i}-${currentChar}-${newChar}${direction !== 'none' ? `-${direction}` : ''}`
      });
    }
    setDigits(newDigits);
    setCurrentPrice(price);
  }, [price, currentPrice, formatter]);
  return jsx(PriceContainer, {
    children: digits.map(digit => jsx(AnimatedDigit, {
      currentDigit: digit.currentDigit,
      targetDigit: digit.targetDigit,
      direction: digit.direction,
      isPositive: digit.isPositive
    }, digit.key))
  });
}, (prevProps, nextProps) => prevProps.price === nextProps.price);

export { PriceRolodex };
